'use client';

import { WindowAdapter } from 'builder/WindowAdapter';
import type { SearchResult, ContentEncoding } from 'conversational-search-client';
import React, { type JSX, useCallback, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { LoggerFactory } from '@videoblocks/typescript-logger';

import { downloadStockItemAction, downloadStockItemPreviewAction } from '../../../_actions/downloadActions';
import { DEFAULT_DOWNLOAD_ERROR_MESSAGE, useChatSessionContext } from '../../../_context/SessionContext';
import { formatFileSize } from '../../../_utils';
import { DownloadAction, ResultCardHoverOverlay } from '../ResultCardOverlay';
import { trackResultEvent } from '../trackResultEvent';
import { type VideoFormat, VideoDownloadPopover } from './VideoDownloadPopover';

const logger = LoggerFactory.create('convSearchVideoResultCard');

interface VideoResultCardProps {
    result: SearchResult;
    isTopResult?: boolean;
}

function formatDuration(totalSeconds: number): string {
    const floored = Math.floor(totalSeconds);
    const minutes = Math.floor(floored / 60);
    const seconds = floored % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// Highest resolution first: format buttons sort by each label's index here.
const RESOLUTION_LABELS_BY_RANK = ['8K', '4K', 'HD', 'SD', ''];

// The label prefixes the format name sent to StockBlocks for download, so these thresholds must
// match StockBlocks' VideoStockItemDTOFactory::getVideoFormatName.
function getResolutionLabel(resolution: number): string {
    if (resolution > 2160) return '8K';
    if (resolution > 1080) return '4K';
    if (resolution >= 720) return 'HD';
    if (resolution > 0) return 'SD';
    return '';
}

function buildFormats(encodings: ContentEncoding[]): VideoFormat[] {
    const best = new Map<VideoFormat['formatName'], Omit<VideoFormat, 'formatName'> & { resolution: number }>();

    for (const enc of encodings) {
        const { format, resolution = 0, fileSizeBytes } = enc;
        const displayFormat = format.toUpperCase();
        const resolutionLabel = getResolutionLabel(resolution);
        const key = `${resolutionLabel}${displayFormat}`;
        const existing = best.get(key);

        // if we have multiple encodings with the same resolution label and format, keep the one with the highest actual resolution
        if (!existing || resolution > existing.resolution) {
            best.set(key, {
                resolution,
                displayFormat,
                resolutionLabel,
                fileSize: formatFileSize(fileSizeBytes),
            });
        }
    }

    return (
        [...best.entries()]
            // don't return format with numeric resolution--only used for ranking/deduping
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            .map(([formatName, { resolution, ...format }]) => ({
                formatName,
                ...format,
            }))
            .sort(
                (a, b) =>
                    RESOLUTION_LABELS_BY_RANK.indexOf(a.resolutionLabel) -
                    RESOLUTION_LABELS_BY_RANK.indexOf(b.resolutionLabel),
            )
    );
}

export function VideoResultCard({ result, isTopResult = false }: VideoResultCardProps): JSX.Element {
    const { canDownloadVideo, setDownloadError } = useChatSessionContext();
    const durationSec = result.duration;
    const href = result.detailsUrl;
    const isPortrait = result.orientation === 'portrait';
    const videoRef = useRef<HTMLVideoElement>(null);
    const [showPopover, setShowPopover] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);

    const formats = useMemo<VideoFormat[]>(
        () => buildFormats(result.downloadEncodings ?? []),
        [result.downloadEncodings],
    );

    const handleMouseEnter = useCallback((): void => {
        const video = videoRef.current;
        if (!video) return;

        video.play().catch((error: unknown) => {
            // Retry once on AbortError (browser autoplay policy race)
            if (error instanceof DOMException && error.name === 'AbortError') {
                videoRef.current?.play().catch(() => {});
            }
        });
    }, []);

    const handleMouseLeave = useCallback((): void => {
        const video = videoRef.current;
        if (!video) return;

        video.pause();
        video.currentTime = 0;
    }, []);

    const handleDownloadClick = useCallback((e: React.MouseEvent): void => {
        e.preventDefault();
        e.stopPropagation();
        setShowPopover(true);
    }, []);

    const handleFormatSelect = useCallback(
        async (formatName: string): Promise<void> => {
            setIsDownloading(true);
            try {
                const downloadResult = await downloadStockItemAction(
                    result.stockblocksId,
                    formatName,
                    result.contentClass,
                );
                if (downloadResult.ok) {
                    trackResultEvent('item.download', result.contentId, isTopResult);
                    WindowAdapter.setLocation(downloadResult.url);
                } else {
                    setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
                }
            } catch (error: unknown) {
                logger.warn(
                    { error, stockblocksId: result.stockblocksId, formatName, contentClass: result.contentClass },
                    'Download action threw on client',
                );
                setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
            } finally {
                setIsDownloading(false);
                setShowPopover(false);
            }
        },
        [result.stockblocksId, result.contentClass, result.contentId, setDownloadError, isTopResult],
    );

    const handlePreviewDownloadClick = useCallback(
        async (e: React.MouseEvent): Promise<void> => {
            e.preventDefault();
            e.stopPropagation();
            if (isDownloading) return;

            setIsDownloading(true);
            try {
                const downloadResult = await downloadStockItemPreviewAction(result.stockblocksId, result.contentClass);
                if (downloadResult.ok) {
                    trackResultEvent('preview.downloaded', result.contentId, isTopResult);
                    WindowAdapter.setLocation(downloadResult.url);
                } else {
                    setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
                }
            } catch (error: unknown) {
                logger.warn(
                    { error, stockblocksId: result.stockblocksId, contentClass: result.contentClass },
                    'Preview download action threw on client',
                );
                setDownloadError(DEFAULT_DOWNLOAD_ERROR_MESSAGE);
            } finally {
                setIsDownloading(false);
            }
        },
        [isDownloading, result.stockblocksId, result.contentClass, result.contentId, setDownloadError, isTopResult],
    );

    const handleClosePopover = useCallback((): void => {
        setShowPopover(false);
    }, []);

    return (
        <div
            className={twMerge('relative size-full overflow-hidden rounded-md bg-gray-20', !showPopover && 'group')}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <a
                href={href}
                onClick={() => trackResultEvent('item.clickthrough', result.contentId, isTopResult)}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute inset-0 z-0"
                aria-label={result.title}
            >
                <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    poster={result.thumbnailUrl}
                    muted
                    loop
                    autoPlay={false}
                    preload="metadata"
                    controls={false}
                    controlsList="nodownload"
                >
                    <source src={result.previewUrl} type="video/mp4" />
                </video>
            </a>

            <ResultCardHoverOverlay
                title={result.title}
                badge={
                    durationSec != null && (
                        <div className="py-0.5 px-1.5 bg-white rounded text-sm leading-5 text-gray-900">
                            <time dateTime={`PT${durationSec}S`}>{formatDuration(durationSec)}</time>
                        </div>
                    )
                }
            />

            <DownloadAction
                isPortrait={isPortrait}
                label={canDownloadVideo ? 'Download Video' : 'Download Preview'}
                disabled={isDownloading}
                onClick={
                    canDownloadVideo
                        ? handleDownloadClick
                        : (e: React.MouseEvent): void => {
                              void handlePreviewDownloadClick(e);
                          }
                }
            />

            {canDownloadVideo && showPopover && (
                <VideoDownloadPopover
                    formats={formats}
                    isDownloading={isDownloading}
                    onSelectFormat={handleFormatSelect}
                    onClose={handleClosePopover}
                />
            )}
        </div>
    );
}
